/**
 * useTreeKeyboard Hook
 *
 * Handles all keyboard interactions for the TreeGrid
 */

import { useEffect, useCallback } from "react";
import { TreeGridColumn } from "../types";

export interface UseTreeKeyboardProps {
  enabled: boolean;
  focusedCell: { rowId: string; columnKey: string } | null;
  editingRowId: string | null;
  /** Default editing row ID from parent - used as fallback when editingRowId isn't set yet */
  defaultEditingRowId?: string | null;
  visibleRows: any[];
  columns: TreeGridColumn[];
  rowsById: Map<string, any>;
  selectedIds: Set<string>;
  /** Whether indent/outdent (Cmd+]/Cmd+[) is enabled */
  enableIndentOutdent?: boolean;
  /** Whether keyboard row creation (Cmd+Enter) is enabled */
  enableKeyboardCreate?: boolean;

  // Callbacks
  onNavigate: (direction: "up" | "down" | "left" | "right") => void;
  onStartEdit: () => void;
  onCommitEdit: () => void;
  onCancelEdit: () => void;
  onToggleExpand: (rowId: string) => void;
  onToggleSelection: (rowId: string) => void;
  onIndent: (rowId: string) => void;
  onOutdent: (rowId: string) => void;
  onCreateRow: (parentId: string | null) => void;
  onDeleteRow: (rowId: string) => void;
  /** Called when Tab/Shift+Tab is pressed on a focused (non-editing) row to
   *  move browser focus to the next/previous component outside the grid. */
  onTab: (shiftKey: boolean) => void;
}

export function useTreeKeyboard(props: UseTreeKeyboardProps) {
  const {
    enabled,
    focusedCell,
    editingRowId,
    defaultEditingRowId,
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    visibleRows,
    columns,
    rowsById,
    enableIndentOutdent = true,
    enableKeyboardCreate = true,
    onNavigate,
    onStartEdit,
    onCommitEdit,
    onCancelEdit,
    onToggleExpand,
    onToggleSelection,
    onIndent,
    onOutdent,
    onCreateRow,
    onDeleteRow,
    onTab,
  } = props;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const modifierKey = isMac ? event.metaKey : event.ctrlKey;

      if (!enabled) return;

      // Ignore keyboard events when user is typing in an input field or textarea
      const target = event.target as HTMLElement;
      const isTypingInInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      // Only allow Escape key to work in inputs (to cancel editing)
      if (isTypingInInput && event.key !== "Escape") {
        return;
      }

      // Consider editing if either editingRowId is set OR defaultEditingRowId is set (for new rows)
      // Use != null to check for both null and undefined
      const isEditing = editingRowId != null || defaultEditingRowId != null;

      // Handle Escape key even without focusedCell - important for canceling new row edits
      if (event.key === "Escape" && isEditing) {
        event.preventDefault();
        onCancelEdit();
        return;
      }

      // For all other keys, require focusedCell
      if (!focusedCell) return;

      const { rowId, columnKey } = focusedCell;
      const row = rowsById.get(rowId);
      const column = columns.find((c) => c.key === columnKey);

      // Don't handle if editing (except for Enter - Escape is handled above)
      if (isEditing && event.key !== "Enter") {
        return;
      }

      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          if (!isEditing) {
            onNavigate("up");
          }
          break;

        case "ArrowDown":
          event.preventDefault();
          if (!isEditing) {
            onNavigate("down");
          }
          break;

        case "ArrowLeft":
          if (!isEditing) {
            event.preventDefault();
            if (row?.isExpanded && row.childrenIds.length > 0) {
              onToggleExpand(rowId);
            } else {
              onNavigate("left");
            }
          }
          break;

        case "ArrowRight":
          if (!isEditing) {
            event.preventDefault();
            if (!row?.isExpanded && row?.childrenIds.length > 0) {
              onToggleExpand(rowId);
            } else {
              onNavigate("right");
            }
          }
          break;

        case "Enter":
          event.preventDefault();
          event.stopPropagation();
          if (isEditing) {
            // Commit the edit; stay focused on the same row (no navigation).
            // The row highlight is preserved — user presses Tab to leave the grid.
            onCommitEdit();
          } else if (modifierKey && enableKeyboardCreate) {
            // Cmd+Enter: Add sibling after the focused row (same parent level)
            const focusedRow = rowsById.get(rowId);
            onCreateRow(focusedRow?.parentId ?? null);
          } else if (modifierKey && !enableKeyboardCreate) {
            // Cmd+Enter disabled - do nothing
            break;
          } else {
            // Plain Enter: always enter edit mode.
            // onStartEdit will jump to the first editable column if the
            // currently focused column is not editable.
            onStartEdit();
          }
          break;

        // Note: Escape is handled at the top of handleKeyDown to work even without focusedCell

        case "Tab":
          // Tab always exits the grid to the next/previous focusable component
          // on the page. onTab handles clearing the focused row and moving
          // browser focus outside the container.
          event.preventDefault();
          onTab(event.shiftKey);
          break;

        case "F2":
          event.preventDefault();
          if (!isEditing && column?.editable) {
            onStartEdit();
          }
          break;

        case " ":
          // Space for selection
          if (!isEditing) {
            event.preventDefault();
            onToggleSelection(rowId);
          }
          break;

        case "Delete":
        case "Backspace":
          if (!isEditing && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            onDeleteRow(rowId);
          }
          break;

        case "]":
          // Indent (make child of previous sibling)
          // Mac: Cmd+] or Ctrl+]
          // Windows: Ctrl+]
          if (!isEditing && modifierKey && enableIndentOutdent) {
            event.preventDefault();
            onIndent(rowId);
          }
          break;

        case "[":
          // Outdent (move to parent's level)
          // Mac: Cmd+[ or Ctrl+[
          // Windows: Ctrl+[
          if (!isEditing && modifierKey && enableIndentOutdent) {
            event.preventDefault();
            onOutdent(rowId);
          }
          break;

        default:
          break;
      }
    },
    [
      enabled,
      focusedCell,
      editingRowId,
      defaultEditingRowId,
      rowsById,
      columns,
      onNavigate,
      onStartEdit,
      onCommitEdit,
      onCancelEdit,
      onToggleExpand,
      onToggleSelection,
      onIndent,
      onOutdent,
      onCreateRow,
      onDeleteRow,
      onTab,
      enableIndentOutdent,
      enableKeyboardCreate,
    ],
  );

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [enabled, handleKeyDown]);
}
